// src/components/CalendarView.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/dashboard/CalendarView.css';   // ← CSS แยก

const CalendarView = ({ events = [], friendsEvents = [] }) => {
  const navigate = useNavigate();

  /* ─────────────────────── state ─────────────────────── */
  const [showDayView,    setShowDayView]    = useState(false);
  const [selectedDate,   setSelectedDate]   = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent]  = useState(null);
  const [currentMonth,   setCurrentMonth]   = useState(new Date().getMonth());
  const [currentYear,    setCurrentYear]    = useState(new Date().getFullYear());
  const today = new Date();

  /* ─────────────────────── helpers ─────────────────────── */
  const daysInMonth     = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const calendarDays    = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  /* group events by yyyy-m-d */
  const groupByDate = (arr, type) =>
    (arr || []).reduce((acc, ev) => {
      if (!ev?.event_date) return acc;
      const d = new Date(ev.event_date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (acc[key] = acc[key] || []).push({ ...ev, type });
      return acc;
    }, {});

  const eventsByDate        = groupByDate(events,        'user');
  const friendsEventsByDate = groupByDate(friendsEvents, 'friend');

  /* ──────────────── calendar navigation ──────────────── */
  const changeMonth = (dir) => {
    const m = dir === 'prev' ? currentMonth - 1 : currentMonth + 1;
    if (m < 0)       { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else if (m > 11) { setCurrentMonth(0);  setCurrentYear(y => y + 1); }
    else             { setCurrentMonth(m); }
  };
  const goToToday = () => {
    const t = new Date();
    setCurrentMonth(t.getMonth());
    setCurrentYear(t.getFullYear());
  };

  /* ───────────────────── EventModal ───────────────────── */
  const EventModal = ({ event, onClose }) => (
    <div className="modal-overlay" onClick={onClose}>
      <div className="event-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{event.event_name || event.event_title || event.title}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p><strong>วันที่:</strong>{' '}
             {new Date(event.event_date).toLocaleDateString('th-TH')}</p>
          {event.event_location && (
            <p><strong>สถานที่:</strong> {event.event_location}</p>
          )}
          {event.event_description && <p>{event.event_description}</p>}
          <button className="calendar-nav-btn today-btn"
                  onClick={() => { navigate(`/event/${event.id}`); onClose(); }}>
            ดูรายละเอียดเต็ม
          </button>
        </div>
      </div>
    </div>
  );

  /* ───────────────────── Day-View modal ───────────────────── */
  const DayView = ({ date, onClose }) => {
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    const allEvents = [
      ...(eventsByDate[key]        || []),
      ...(friendsEventsByDate[key] || []),
    ].sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="day-view-modal" onClick={(e) => e.stopPropagation()}>
          {/* sticky header */}
          <div className="modal-header">
            <h3>
              {date.toLocaleDateString('th-TH', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

          <div className="modal-body">
            {allEvents.length === 0 ? (
              <div className="no-events">ไม่มีกิจกรรมในวันนี้</div>
            ) : (
              <div className="day-events-list">
                {allEvents.map((ev, i) => {
                  const title =
                        ev.event_name     || ev.event_title || ev.title ||
                        ev.name           || ev.name_th     || ev.name_en ||
                        '— ไม่มีชื่อกิจกรรม —';
                  const place =
                        ev.event_location || ev.venue_name  || ev.location ||
                        ev.address        || ev.venue       || ev.place   || '';

                  return (
                    <div key={i}
                         className={`day-event-card ${ev.type}${ev.type==='user' && ev.status==='going' ? ' going' : ''}`}
                         onClick={() => { 
                           // Navigate directly to event detail page
                           navigate(`/event/${ev.id}`);
                           onClose(); // Close the day view modal
                         }}>
                      {/* badge */}
                      <div className="event-status-badge">
                        {ev.type === 'friend' ? 'เพื่อน' : 'คุณ'}
                      </div>

                      {/* content */}
                      <div className="event-content">
                        <h4 className="event-name-clickable">{title}</h4>

                        {ev.type === 'friend' ? (
                          <div className="friend-going">
                            👥 <strong>{ev.friend_name}</strong> จะไปงานนี้
                          </div>
                        ) : (
                          <div className="user-status-compact">
                            {ev.status === 'going' ? '💖' : '⭐'}{' '}
                            {ev.status === 'going' ? 'คุณจะเข้าร่วม' : 'คุณสนใจ'}
                          </div>
                        )}

                        {place && (
                          <div className="event-meta">
                            <div className="event-location-info">📍 {place}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ───────────────────── Calendar render ───────────────────── */
  return (
    <>
      <div className="calendar-view">
        {/* header */}
        <div className="calendar-header">
          <h3>
            {new Date(currentYear, currentMonth)
              .toLocaleDateString('th-TH', { month: 'long', year: 'numeric' })}
          </h3>
          <div className="calendar-nav">
            <button className="calendar-nav-btn"
                    onClick={() => changeMonth('prev')}>‹</button>
            <button className="calendar-nav-btn today-btn"
                    onClick={goToToday}>วันนี้</button>
            <button className="calendar-nav-btn"
                    onClick={() => changeMonth('next')}>›</button>
          </div>
        </div>

        {/* weekday labels */}
        <div className="calendar-weekdays">
          {['อา','จ','อ','พ','พฤ','ศ','ส'].map((d, i) =>
            <div key={i} className="weekday">{d}</div>)}
        </div>

        {/* day cells */}
        <div className="calendar-days">
          {/* leading blanks */}
          {Array.from({ length: firstDayOfMonth }).map((_, i) =>
            <div key={`e${i}`} className="calendar-day empty" />)}

          {calendarDays.map((day) => {
            const key = `${currentYear}-${currentMonth}-${day}`;
            const userCount   = eventsByDate[key]?.length        || 0;
            const friendCount = friendsEventsByDate[key]?.length || 0;
            const isToday = day === today.getDate()
                            && currentMonth === today.getMonth()
                            && currentYear  === today.getFullYear();

            return (
              <div key={day}
                   className={`calendar-day ${isToday ? 'today' : ''}`}
                   onClick={() => {
                     setSelectedDate(new Date(currentYear, currentMonth, day));
                     setShowDayView(true);
                   }}>
                <span className="day-number">{day}</span>

                {/* user dots (สีเขียว / ม่วง) */}
                <div className="event-dots">
                  {(eventsByDate[key] || []).slice(0, 3).map((ev, i) => (
                    <span key={i}
                          className={`event-dot user-event ${ev.status === 'going' ? 'going' : ''}`}
                          title={ev.event_name || ev.title}
                          onClick={(e) => { e.stopPropagation();
                              setSelectedDate(new Date(currentYear,currentMonth,day));
                              setShowDayView(true); }} />
                  ))}
                  {userCount > 3 && (
                    <span className="more-events"
                          onClick={(e) => { e.stopPropagation();
                              setSelectedDate(new Date(currentYear,currentMonth,day));
                              setShowDayView(true); }}>
                      +{userCount - 3}
                    </span>
                  )}
                </div>

                {/* friend labels (สีเหลือง) */}
                {friendCount > 0 && (
                  <div className="friends-events">
                    {friendsEventsByDate[key].slice(0, 2).map((fe, i) => (
                      <span key={i}
                            className="friend-event-name"
                            title={`${fe.friend_name} จะไปงาน ${fe.event_name || fe.event_title}`}
                            onClick={(e) => { e.stopPropagation();
                                setSelectedDate(new Date(currentYear,currentMonth,day));
                                setShowDayView(true); }}>
                        {fe.friend_name}
                      </span>
                    ))}
                    {friendCount > 2 && (
                      <span className="more-friends"
                            onClick={(e) => { e.stopPropagation();
                                setSelectedDate(new Date(currentYear,currentMonth,day));
                                setShowDayView(true); }}>
                        +{friendCount - 2}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* legend / foot-note */}
        <div className="calendar-legend">
          <div className="legend-item">
            <span className="event-dot user-event" />
            <span>กิจกรรมที่คุณสนใจ</span>
          </div>
          <div className="legend-item">
            <span className="event-dot user-event going" />
            <span>กิจกรรมที่คุณจะเข้าร่วม</span>
          </div>
          <div className="legend-item">
            <span className="friend-event-sample">เพื่อน</span>
            <span>กิจกรรมของเพื่อน</span>
          </div>
        </div>
      </div>

      {/* modals */}
      {showDayView    && selectedDate  && (
        <DayView date={selectedDate} onClose={() => setShowDayView(false)} />
      )}
      {showEventModal && selectedEvent && (
        <EventModal event={selectedEvent} onClose={() => setShowEventModal(false)} />
      )}
    </>
  );
};

export default CalendarView;